import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Button, Tag, Typography, Space, Tooltip } from 'antd';
import {
    HistoryOutlined,
    AimOutlined,
    RocketOutlined,
    WarningOutlined,
    CheckCircleOutlined,
    InfoCircleOutlined,
    AlertOutlined,
    BulbOutlined,
    CloseOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { partitionInsights } from '../../utils/insightsDismiss';
import './InsightsFinanceirosCard.css';

const { Text, Title } = Typography;

const HORIZONTE_META = {
    passado: { label: 'Passado', icon: <HistoryOutlined />, className: 'insight-horizonte--passado' },
    presente: { label: 'Presente', icon: <AimOutlined />, className: 'insight-horizonte--presente' },
    futuro: { label: 'Futuro', icon: <RocketOutlined />, className: 'insight-horizonte--futuro' },
};

const SEVERIDADE_META = {
    critico: { label: 'Crítico', color: 'error', icon: <AlertOutlined /> },
    atencao: { label: 'Atenção', color: 'warning', icon: <WarningOutlined /> },
    neutro: { label: 'Info', color: 'default', icon: <InfoCircleOutlined /> },
    positivo: { label: 'Positivo', color: 'success', icon: <CheckCircleOutlined /> },
};

const MAX_VISIVEIS = 5;

/**
 * Mascara valores monetários (R$ …) e percentuais soltos quando privacy está ativo.
 */
function mascararMensagem(mensagem, isPrivate) {
    if (!isPrivate || !mensagem) return mensagem;
    return String(mensagem)
        .replace(/R\$\s*[\d.]+(?:,\d+)?/g, '****')
        .replace(/(\d+(?:[.,]\d+)?)\s*%/g, '****%')
        .replace(/(\d+(?:[.,]\d+)?)\s*pontos?/gi, '**** pontos')
        .replace(/cerca de (\d+(?:[.,]\d+)?)\s*mês/gi, 'cerca de **** mês');
}

/**
 * @param {{
 *   insights: Array,
 *   isPrivate: boolean,
 *   dismissedMap?: Record<string, string>,
 *   onDismiss?: (insightId: string) => void,
 *   onRestoreHidden?: (insightIds: string[]) => void,
 *   busy?: boolean,
 * }} props
 */
export default function InsightsFinanceirosCard({
    insights,
    isPrivate,
    dismissedMap = {},
    onDismiss,
    onRestoreHidden,
    busy = false,
}) {
    const navigate = useNavigate();
    const { visible, hidden } = useMemo(
        () => partitionInsights(insights, dismissedMap),
        [insights, dismissedMap],
    );
    const lista = visible.slice(0, MAX_VISIVEIS);
    const hiddenIds = hidden.map((h) => h.id);

    if (lista.length === 0 && hidden.length === 0) {
        return null;
    }

    if (lista.length === 0 && hidden.length > 0) {
        return (
            <Card
                className="insights-financeiros-card insights-financeiros-card--compact"
                bordered={false}
            >
                <div className="insights-financeiros-hidden-banner">
                    <Space size={8}>
                        <BulbOutlined className="insights-financeiros-title-icon" />
                        <Text>
                            {hidden.length === 1
                                ? '1 insight oculto'
                                : `${hidden.length} insights ocultos`}
                        </Text>
                    </Space>
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        disabled={busy || !onRestoreHidden}
                        onClick={() => onRestoreHidden?.(hiddenIds)}
                    >
                        Mostrar novamente
                    </Button>
                </div>
            </Card>
        );
    }

    return (
        <Card
            className="insights-financeiros-card"
            bordered={false}
            title={
                <Space size={8}>
                    <BulbOutlined className="insights-financeiros-title-icon" />
                    <span>Insights financeiros</span>
                </Space>
            }
            extra={
                <Space size={8} className="insights-financeiros-extra">
                    <Text type="secondary">Passado · Presente · Futuro</Text>
                    {hidden.length > 0 ? (
                        <Button
                            type="link"
                            size="small"
                            icon={<EyeOutlined />}
                            disabled={busy || !onRestoreHidden}
                            onClick={() => onRestoreHidden?.(hiddenIds)}
                        >
                            {hidden.length === 1
                                ? '1 oculto'
                                : `${hidden.length} ocultos`}
                        </Button>
                    ) : null}
                </Space>
            }
        >
            <ul className="insights-financeiros-list">
                {lista.map((item) => {
                    const horiz = HORIZONTE_META[item.horizonte] || HORIZONTE_META.presente;
                    const sev = SEVERIDADE_META[item.severidade] || SEVERIDADE_META.neutro;
                    const rota = item.cta?.rota;
                    const labelCta = item.cta?.label || 'Ver mais';
                    return (
                        <li
                            key={item.id}
                            className={`insights-financeiros-item insights-financeiros-item--${item.severidade || 'neutro'}`}
                        >
                            <div className="insights-financeiros-item-head">
                                <div className="insights-financeiros-item-tags">
                                    <Tag
                                        className={`insight-horizonte-tag ${horiz.className}`}
                                        icon={horiz.icon}
                                    >
                                        {horiz.label}
                                    </Tag>
                                    <Tag color={sev.color} icon={sev.icon}>
                                        {sev.label}
                                    </Tag>
                                </div>
                                {onDismiss ? (
                                    <Tooltip title="Dispensar por 7 dias">
                                        <Button
                                            type="text"
                                            size="small"
                                            className="insights-financeiros-dismiss"
                                            icon={<CloseOutlined />}
                                            aria-label="Dispensar insight"
                                            disabled={busy}
                                            onClick={() => onDismiss(item.id)}
                                        />
                                    </Tooltip>
                                ) : null}
                            </div>
                            <Title level={5} className="insights-financeiros-item-title">
                                {item.titulo}
                            </Title>
                            <Text className="insights-financeiros-item-msg">
                                {mascararMensagem(item.mensagem, isPrivate)}
                            </Text>
                            <div className="insights-financeiros-item-actions">
                                {rota ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        onClick={() => navigate(rota)}
                                    >
                                        {labelCta}
                                    </Button>
                                ) : null}
                                {onDismiss ? (
                                    <Button
                                        type="link"
                                        size="small"
                                        disabled={busy}
                                        onClick={() => onDismiss(item.id)}
                                    >
                                        Entendi
                                    </Button>
                                ) : null}
                            </div>
                        </li>
                    );
                })}
            </ul>
        </Card>
    );
}
